import { CURRENT_USER_ACCESS_TTL_MS } from './currentUserAccessService';

/**
 * Shared periodic revalidation clock for capability-access consumers that
 * have no backend evidence-refresh cycle to key off of (Keycloak-token-roles
 * mode and local-auth mode - see useCapabilityAccess's accessEvidenceVersion,
 * which is a constant in those modes otherwise). All subscribers share one
 * setInterval and one epoch counter instead of each consumer (e.g. every
 * mounted RTUActions row) running its own timer: the interval ticks every
 * CURRENT_USER_ACCESS_TTL_MS (30s, the same cadence as the backend-mode
 * current-user cache), increments the epoch once, and notifies every current
 * subscriber with the new value in the same tick - so a refetch it drives
 * (e.g. RTU action hints) batches into one request regardless of how many
 * consumers are mounted. The interval exists only while >=1 subscriber is
 * registered and is cleared the moment the last one unsubscribes, so nothing
 * leaks once every consumer unmounts.
 */
let epoch = 0;
let intervalId: ReturnType<typeof setInterval> | undefined;
const listeners = new Set<() => void>();

const tick = () => {
  epoch += 1;
  listeners.forEach((listener) => listener());
};

const accessRevalidationClock = {
  getEpoch(): number {
    return epoch;
  },

  subscribe(listener: () => void): () => void {
    listeners.add(listener);
    if (intervalId === undefined) {
      intervalId = setInterval(tick, CURRENT_USER_ACCESS_TTL_MS);
    }
    return () => {
      listeners.delete(listener);
      if (listeners.size === 0 && intervalId !== undefined) {
        clearInterval(intervalId);
        intervalId = undefined;
      }
    };
  },
};

export default accessRevalidationClock;
